import { useEffect, useState } from 'react';
import { bancosService } from '../services/api';
import '../styles/Clientes.css';

const Bancos = () => {
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    numero: '',
    tipo: 'BAN',
    balance: '0',
    limite: '0',
    oficial: '',
    telefono: '',
    extension: '',
    corte: ''
  });
  const [editingId, setEditingId] = useState(null);
  const today = new Date().toISOString().split('T')[0];
  const [modalOperacion, setModalOperacion] = useState(null);
  const [bancoSeleccionado, setBancoSeleccionado] = useState(null);
  const [operacionData, setOperacionData] = useState({
    fecha: today,
    monto: '',
    concepto: '',
    referencia: '',
    detalle: ''
  });
  const [operacionLoading, setOperacionLoading] = useState(false);
  const [operacionError, setOperacionError] = useState('');

  const [mostrarEstado, setMostrarEstado] = useState(false);
  const [estadoParams, setEstadoParams] = useState({ desde: today, hasta: today });
  const [estadoResultados, setEstadoResultados] = useState(null);
  const [estadoLoading, setEstadoLoading] = useState(false);
  const [estadoError, setEstadoError] = useState('');

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user')) || null;
    } catch (err) {
      return null;
    }
  })();
  const userRole = (currentUser?.rol || currentUser?.role || '').toLowerCase();
  const isAdmin = ['admin', 'administrador', 'super_admin', 'superadmin'].includes(userRole);

  useEffect(() => {
    cargarBancos();
  }, []);

  const cargarBancos = async () => {
    try {
      setLoading(true);
      const response = await bancosService.getBancos();
      setBancos(response.data.bancos || []);
    } catch (error) {
      console.error('Error al cargar bancos:', error);
      setError('Error al cargar cuentas bancarias');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    const value = Number(amount) || 0;
    return `$${value.toLocaleString('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const getTipoBadge = (tipo) => {
    const colores = {
      BAN: '#3498db',
      EFE: '#27ae60',
      TAR: '#f39c12'
    };

    const nombres = {
      BAN: 'Banco',
      EFE: 'Efectivo',
      TAR: 'Tarjeta'
    };

    return (
      <span style={{
        backgroundColor: colores[tipo] || '#95a5a6',
        color: '#fff',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '0.85rem'
      }}>
        {nombres[tipo] || tipo}
      </span>
    );
  };

  const handleOpenModal = (banco = null) => {
    if (banco) {
      setEditingId(banco.id);
      setFormData({
        nombre: banco.nombre || '',
        numero: banco.numero || '',
        tipo: banco.tipo || 'BAN',
        balance: banco.balance || '0',
        limite: banco.limite || '0',
        oficial: banco.oficial || '',
        telefono: banco.telefono || '',
        extension: banco.extension || '',
        corte: banco.corte || ''
      });
    } else {
      setEditingId(null);
      setFormData({
        nombre: '',
        numero: '',
        tipo: 'BAN',
        balance: '0',
        limite: '0',
        oficial: '',
        telefono: '',
        extension: '',
        corte: ''
      });
    }
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      nombre: '',
      numero: '',
      tipo: 'BAN',
      balance: '0',
      limite: '0',
      oficial: '',
      telefono: '',
      extension: '',
      corte: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setLoading(true);

      const dataToSend = {
        nombre: formData.nombre,
        numero: formData.numero,
        tipo: formData.tipo,
        balance: parseFloat(formData.balance) || 0,
        limite: parseFloat(formData.limite) || 0,
        oficial: formData.oficial,
        telefono: formData.telefono,
        extension: formData.extension,
        corte: formData.corte || null
      };

      if (editingId) {
        await bancosService.updateBanco(editingId, dataToSend);
        setSuccess('Cuenta bancaria actualizada correctamente');
      } else {
        await bancosService.createBanco(dataToSend);
        setSuccess('Cuenta bancaria creada correctamente');
      }

      handleCloseModal();
      await cargarBancos();
    } catch (error) {
      console.error('Error al guardar cuenta bancaria:', error);
      setError(error.response?.data?.message || error.response?.data?.error || 'Error al guardar la cuenta bancaria');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta cuenta bancaria?')) {
      return;
    }

    try {
      setLoading(true);
      await bancosService.deleteBanco(id);
      setSuccess('Cuenta bancaria eliminada correctamente');
      await cargarBancos();
    } catch (error) {
      console.error('Error al eliminar cuenta bancaria:', error);
      setError(error.response?.data?.error || 'Error al eliminar la cuenta bancaria');
    } finally {
      setLoading(false);
    }
  };

  const openOperacionModal = (banco, tipo, transaccion = null) => {
    setBancoSeleccionado(banco);
    setModalOperacion(tipo);
    setOperacionError('');
    setOperacionLoading(false);

    if (transaccion) {
      setOperacionData({
        id: transaccion.id,
        fecha: transaccion.fecha ? transaccion.fecha.substring(0, 10) : today,
        monto: transaccion.monto != null ? transaccion.monto : '',
        concepto: transaccion.concepto || (tipo === 'deposito' ? 'Depósito a cuenta' : 'Retiro para préstamos'),
        referencia: transaccion.referencia || '',
        detalle: transaccion.detalle || ''
      });
    } else {
      setOperacionData({
        fecha: today,
        monto: '',
        concepto: tipo === 'deposito' ? 'Depósito a cuenta' : 'Retiro para préstamos',
        referencia: '',
        detalle: ''
      });
    }
  };

  const closeOperacionModal = () => {
    setModalOperacion(null);
    setOperacionError('');
    setOperacionLoading(false);
    setOperacionData({
      fecha: today,
      monto: '',
      concepto: '',
      referencia: '',
      detalle: ''
    });
  };

  const handleOperacionChange = (e) => {
    const { name, value } = e.target;
    setOperacionData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitOperacion = async (e) => {
    e.preventDefault();
    if (!bancoSeleccionado || !modalOperacion) return;

    const montoNumber = parseFloat(operacionData.monto);
    if (Number.isNaN(montoNumber) || montoNumber <= 0) {
      setOperacionError('El monto debe ser mayor a 0');
      return;
    }

    const payload = {
      fecha: operacionData.fecha,
      monto: montoNumber,
      concepto: operacionData.concepto,
      referencia: operacionData.referencia,
      detalle: operacionData.detalle
    };

    setOperacionLoading(true);
    setOperacionError('');
    try {
      if (operacionData.id) {
        await bancosService.updateTransaccion(bancoSeleccionado.id, operacionData.id, payload);
        setSuccess(modalOperacion === 'deposito' ? 'Depósito actualizado correctamente' : 'Retiro actualizado correctamente');
      } else {
        const serviceFn = modalOperacion === 'deposito'
          ? bancosService.depositar
          : bancosService.retirar;
        await serviceFn(bancoSeleccionado.id, payload);
        setSuccess(modalOperacion === 'deposito' ? 'Depósito registrado correctamente' : 'Retiro registrado correctamente');
      }
      setError('');
      closeOperacionModal();
      await cargarBancos();
      if (mostrarEstado && bancoSeleccionado) {
        consultarEstadoCuenta(null, estadoParams, bancoSeleccionado);
      }
    } catch (err) {
      console.error('Error registrando operación bancaria:', err);
      setOperacionError(err.response?.data?.message || err.response?.data?.error || 'Error al registrar la operación');
    } finally {
      setOperacionLoading(false);
    }
  };

  const abrirEstadoCuenta = (banco) => {
    const defaultParams = { desde: today, hasta: today };
    setBancoSeleccionado(banco);
    setMostrarEstado(true);
    setEstadoParams(defaultParams);
    setEstadoResultados(null);
    setEstadoError('');
    setTimeout(() => {
      consultarEstadoCuenta(null, defaultParams, banco);
    }, 0);
  };

  const cerrarEstadoCuenta = () => {
    setMostrarEstado(false);
    setBancoSeleccionado(null);
    setEstadoResultados(null);
    setEstadoLoading(false);
    setEstadoError('');
  };

  const handleEstadoChange = (e) => {
    const { name, value } = e.target;
    setEstadoParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const consultarEstadoCuenta = async (e, paramsOverride, bancoOverride) => {
    if (e) {
      e.preventDefault();
    }
    const bancoObjetivo = bancoOverride || bancoSeleccionado;
    if (!bancoObjetivo) return;

    const params = paramsOverride || estadoParams;

    if (params.desde && params.hasta && new Date(params.desde) > new Date(params.hasta)) {
      setEstadoError('La fecha "Desde" no puede ser mayor que "Hasta"');
      return;
    }

    setEstadoLoading(true);
    setEstadoError('');
    try {
      const response = await bancosService.getEstadoCuenta(bancoObjetivo.id, params);
      setEstadoResultados(response.data);
    } catch (err) {
      console.error('Error consultando estado de cuenta:', err);
      setEstadoError(err.response?.data?.message || err.response?.data?.error || 'Error al obtener el estado de cuenta');
    } finally {
      setEstadoLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString('es-DO');
  };

  const editarTransaccion = (transaccion) => {
    if (!transaccion || !bancoSeleccionado) return;
    const tipo = transaccion.tipo === 'ingreso' ? 'deposito' : 'retiro';
    openOperacionModal(bancoSeleccionado, tipo, transaccion);
  };

  const eliminarTransaccion = async (transaccion) => {
    if (!transaccion || !bancoSeleccionado) return;
    if (!window.confirm('¿Deseas eliminar esta transacción?')) {
      return;
    }

    setEstadoLoading(true);
    setEstadoError('');
    try {
      await bancosService.deleteTransaccion(bancoSeleccionado.id, transaccion.id);
      setSuccess('Transacción eliminada correctamente');
      await cargarBancos();
      consultarEstadoCuenta(null, estadoParams, bancoSeleccionado);
    } catch (err) {
      console.error('Error eliminando transacción:', err);
      setEstadoError(err.response?.data?.message || err.response?.data?.error || 'Error al eliminar la transacción');
    } finally {
      setEstadoLoading(false);
    }
  };

  const isEditOperacion = Boolean(operacionData.id);

  if (loading) return <div className="loading">Cargando cuentas bancarias...</div>;

  return (
    <div className="clientes-page">
      <div className="clientes-header">
        <h1>🏦 Cuentas Bancarias</h1>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          + Nueva Cuenta
        </button>
      </div>

      {error && <div className="banner error">{error}</div>}
      {success && <div className="banner success">{success}</div>}

      {/* Tabla de Bancos */}
      <div className="card">
        <div className="card-title">
          Cuentas Bancarias ({bancos.length})
        </div>

        {bancos.length === 0 ? (
          <p style={{padding: '2rem', textAlign: 'center', color: '#666'}}>
            No hay cuentas bancarias registradas
          </p>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Banco</th>
                  <th>Número de Cuenta</th>
                  <th>Tipo</th>
                  <th>Balance</th>
                  <th>Oficial</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {bancos.map((banco) => (
                  <tr key={banco.id}>
                    <td>{banco.id}</td>
                    <td style={{fontWeight: 'bold'}}>{banco.nombre}</td>
                    <td>{banco.numero || 'N/A'}</td>
                    <td>{getTipoBadge(banco.tipo)}</td>
                    <td style={{fontWeight: 'bold', color: banco.balance >= 0 ? '#27ae60' : '#e74c3c'}}>
                      {formatMoney(banco.balance)}
                    </td>
                    <td>{banco.oficial || 'N/A'}</td>
                    <td>
                      <button
                        className="btn-icon"
                        title="Registrar depósito"
                        onClick={() => openOperacionModal(banco, 'deposito')}
                      >
                        ➕
                      </button>
                      <button
                        className="btn-icon"
                        title="Registrar retiro"
                        onClick={() => openOperacionModal(banco, 'retiro')}
                        style={{marginLeft: '0.5rem'}}
                      >
                        ➖
                      </button>
                      <button
                        className="btn-icon"
                        title="Estado de cuenta"
                        onClick={() => abrirEstadoCuenta(banco)}
                        style={{marginLeft: '0.5rem'}}
                      >
                        📄
                      </button>
                      <button
                        className="btn-icon"
                        title="Editar"
                        onClick={() => handleOpenModal(banco)}
                        style={{marginLeft: '0.5rem'}}
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon"
                        title="Eliminar"
                        onClick={() => handleDelete(banco.id)}
                        style={{marginLeft: '0.5rem'}}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal para crear/editar cuenta bancaria */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Nombre del Banco *</label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    placeholder="Ej: Banco Popular"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Número de Cuenta</label>
                  <input
                    type="text"
                    name="numero"
                    value={formData.numero}
                    onChange={handleInputChange}
                    placeholder="Ej: 123456789"
                  />
                </div>

                <div className="form-group">
                  <label>Tipo de Cuenta *</label>
                  <select
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="BAN">Banco</option>
                    <option value="EFE">Efectivo</option>
                    <option value="TAR">Tarjeta</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Balance Inicial *</label>
                  <input
                    type="number"
                    name="balance"
                    value={formData.balance}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Límite de Crédito</label>
                  <input
                    type="number"
                    name="limite"
                    value={formData.limite}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label>Oficial de Cuenta</label>
                  <input
                    type="text"
                    name="oficial"
                    value={formData.oficial}
                    onChange={handleInputChange}
                    placeholder="Nombre del oficial"
                  />
                </div>

                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    placeholder="Ej: 809-555-5555"
                  />
                </div>

                <div className="form-group">
                  <label>Extensión</label>
                  <input
                    type="text"
                    name="extension"
                    value={formData.extension}
                    onChange={handleInputChange}
                    placeholder="Ej: 1234"
                  />
                </div>

                <div className="form-group">
                  <label>Fecha de Corte</label>
                  <input
                    type="date"
                    name="corte"
                    value={formData.corte}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Guardando...' : (editingId ? 'Actualizar' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalOperacion && (
        <div className="modal-overlay" onClick={closeOperacionModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {isEditOperacion
                  ? (modalOperacion === 'deposito' ? 'Editar Depósito' : 'Editar Retiro')
                  : (modalOperacion === 'deposito' ? 'Registrar Depósito' : 'Registrar Retiro')}
                {bancoSeleccionado ? ` - ${bancoSeleccionado.nombre}` : ''}
              </h2>
              <button className="modal-close" onClick={closeOperacionModal}>×</button>
            </div>

            <form onSubmit={submitOperacion}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Fecha *</label>
                  <input
                    type="date"
                    name="fecha"
                    value={operacionData.fecha}
                    onChange={handleOperacionChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Monto *</label>
                  <input
                    type="number"
                    name="monto"
                    value={operacionData.monto}
                    onChange={handleOperacionChange}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Concepto *</label>
                  <input
                    type="text"
                    name="concepto"
                    value={operacionData.concepto}
                    onChange={handleOperacionChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Referencia</label>
                  <input
                    type="text"
                    name="referencia"
                    value={operacionData.referencia}
                    onChange={handleOperacionChange}
                    placeholder="Referencia opcional"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Detalle</label>
                <textarea
                  name="detalle"
                  value={operacionData.detalle}
                  onChange={handleOperacionChange}
                  rows={3}
                  placeholder="Notas o detalle de la operación"
                />
              </div>

              {operacionError && <div className="banner error">{operacionError}</div>}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeOperacionModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={operacionLoading}>
                  {operacionLoading ? 'Guardando...' : (isEditOperacion ? 'Actualizar' : 'Registrar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {mostrarEstado && (
        <div className="modal-overlay" onClick={cerrarEstadoCuenta}>
          <div className="modal-content" style={{maxWidth: '900px'}} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                Estado de Cuenta
                {bancoSeleccionado ? ` - ${bancoSeleccionado.nombre}` : ''}
              </h2>
              <button className="modal-close" onClick={cerrarEstadoCuenta}>×</button>
            </div>

            <form onSubmit={consultarEstadoCuenta} className="form-grid" style={{gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))'}}>
              <div className="form-group">
                <label>Desde</label>
                <input
                  type="date"
                  name="desde"
                  value={estadoParams.desde}
                  onChange={handleEstadoChange}
                />
              </div>
              <div className="form-group">
                <label>Hasta</label>
                <input
                  type="date"
                  name="hasta"
                  value={estadoParams.hasta}
                  onChange={handleEstadoChange}
                />
              </div>
              <div className="form-group" style={{alignSelf: 'end'}}>
                <button type="submit" className="btn btn-primary" disabled={estadoLoading}>
                  {estadoLoading ? 'Consultando...' : 'Aplicar'}
                </button>
              </div>
            </form>

            {estadoError && <div className="banner error">{estadoError}</div>}

            {estadoLoading && <div className="loading" style={{marginTop: '1rem'}}>Cargando estado de cuenta...</div>}

            {!estadoLoading && estadoResultados && (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '1rem',
                    marginTop: '1.5rem'
                  }}
                >
                  <div className="card" style={{padding: '1rem'}}>
                    <span>Saldo Inicial</span>
                    <h3 style={{marginTop: '0.5rem'}}>{formatMoney(estadoResultados.saldo_inicial)}</h3>
                  </div>
                  <div className="card" style={{padding: '1rem'}}>
                    <span>Ingresos</span>
                    <h3 style={{marginTop: '0.5rem', color: '#27ae60'}}>{formatMoney(estadoResultados.total_ingresos)}</h3>
                  </div>
                  <div className="card" style={{padding: '1rem'}}>
                    <span>Egresos</span>
                    <h3 style={{marginTop: '0.5rem', color: '#e74c3c'}}>{formatMoney(estadoResultados.total_egresos)}</h3>
                  </div>
                  <div className="card" style={{padding: '1rem'}}>
                    <span>Saldo Final</span>
                    <h3 style={{marginTop: '0.5rem'}}>{formatMoney(estadoResultados.saldo_final)}</h3>
                  </div>
                </div>

                <div style={{overflowX: 'auto', marginTop: '1.5rem'}}>
                  {estadoResultados.transacciones && estadoResultados.transacciones.length > 0 ? (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Concepto</th>
                          <th>Tipo</th>
                          <th>Monto</th>
                          <th>Balance Anterior</th>
                          <th>Balance Actual</th>
                          <th>Usuario</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {estadoResultados.transacciones.map((tx) => {
                          const esIngreso = tx.tipo === 'ingreso';
                          return (
                            <tr key={tx.id}>
                              <td>{formatDate(tx.fecha)}</td>
                              <td>{tx.concepto}</td>
                              <td style={{fontWeight: 'bold'}}>{esIngreso ? 'Ingreso' : 'Egreso'}</td>
                              <td style={{color: esIngreso ? '#27ae60' : '#e74c3c'}}>
                                {formatMoney(tx.monto)}
                              </td>
                              <td>{formatMoney(tx.balance_anterior)}</td>
                              <td>{formatMoney(tx.balance_actual)}</td>
                              <td>{tx.usuario || 'N/A'}</td>
                              <td>
                                {isAdmin && (tx.tipo === 'ingreso' || tx.tipo === 'egreso') && (
                                  <>
                                    <button
                                      className="btn-icon"
                                      title="Editar"
                                      onClick={() => editarTransaccion(tx)}
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      className="btn-icon"
                                      title="Eliminar"
                                      onClick={() => eliminarTransaccion(tx)}
                                      style={{marginLeft: '0.5rem'}}
                                    >
                                      🗑️
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{textAlign: 'center', color: '#666'}}>No hay transacciones en el rango seleccionado</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Bancos;
